import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Send, Eye, LogOut, Users, Mail, BarChart3, BookOpen, TestTube, History, Sparkles, Clock, XCircle, ChevronUp, ChevronDown, MoveVertical, Wand2 } from "lucide-react";
import { API_URL, BRAND } from "../lib/brand";

const DRAFT_STORAGE_KEY = "tv_newsletter_composer_draft";

const emptyForm = {
  subject: "C'è una nuova lettera per te 💗",
  preheader: "Uno spazio per fermarci, osservare e accompagnare l'infanzia con uno sguardo diverso.",
  issue_number: "NUMERO 0 – BENVENUTA/O",
  greeting: "Ciao,",
  body_html: "<p>se questa lettera è arrivata nella tua casella, da oggi abbiamo un piccolo spazio in più in cui incontrarci.</p>\n<p><strong>L'ho chiamato \"Una lettera alla volta\".</strong></p>\n<p>Avrei potuto chiamarla semplicemente newsletter. Ma non era quello che volevo.</p>\n<p>Perché qui non voglio soltanto raccontarti cosa pubblico, quali servizi offro o ricordarti che è uscito un nuovo contenuto. <em>Vorrei scriverti.</em> Come si scrive a qualcuno quando c'è un pensiero che merita qualche minuto in più.</p>\n<p>Vorrei che queste lettere diventassero uno spazio in cui fermarci, ogni tanto, nel rumore dei tanti consigli sull'infanzia.</p>\n<p>Uno spazio per <strong>osservare</strong>. Per farci domande. Per trovare qualche strumento concreto. E, soprattutto, per provare a guardare bambine e bambini un po' più dalla loro altezza.</p>",
  highlight_title: "Perché una lettera?",
  highlight_body: "<p><em>L'editoriale di Teresa</em></p>\n<p>Da più di vent'anni entro, in punta di piedi, nelle case e nella quotidianità delle famiglie attraverso il mio lavoro con i bambini. E una cosa continua a sorprendermi.</p>\n<p>Dietro domande apparentemente semplici come:</p>\n<ul><li>«Perché non dorme?»</li><li>«Perché non mi ascolta?»</li><li>«Devo aiutarlo o lasciarlo fare?»</li><li>«Sto facendo bene?»</li></ul>\n<p>spesso ce n'è un'altra, molto più profonda: <strong>«Come posso stare accanto a questo bambino senza perdere di vista lui… e senza perdere me?»</strong></p>\n<p>Questa lettera nasce proprio da qui. Non per aggiungere altri \"devi\", ma per creare uno spazio in cui parlare di infanzia, relazione ed educazione con rispetto, concretezza e cura.</p>",
  quote: "L'osservazione è uno dei molti mezzi che abbiamo per aiutare il bambino. — Maria Montessori",
  chat_body: "<p>Ogni lettera partirà da qualcosa di vero.</p>\n<p>Una frase ascoltata durante il mio lavoro. Un episodio accaduto in una casa. Una domanda di una mamma, di un papà o di una tata. Un comportamento di un bambino che mi ha fatto fermare a pensare.</p>\n<p>Perché non voglio raccontarti un'educazione perfetta. <strong>Voglio parlare dell'educazione che succede davvero.</strong></p>",
  sguardo_body: "<p>In ogni lettera prenderemo uno di quei piccoli grandi temi che attraversano l'infanzia e proveremo a guardarlo un po' più da vicino.</p>\n<p><em>Sonno · Gioco · Autonomie · Limiti · Alimentazione complementare · Ciuccio e pannolino · Linguaggio · Routine · Emozioni · Relazione tra adulti e bambini.</em></p>\n<p>Non per cercare la ricetta giusta. Ma per capire cosa potrebbe esserci dietro ciò che vediamo.</p>",
  tip_title: "Piccola cosa da provare già oggi",
  tip_body: "<p>Regalati <strong>cinque minuti di osservazione</strong>. Scegli un momento qualsiasi in cui sei accanto a un bambino. Per cinque minuti prova a:</p>\n<ul><li>non guidare</li><li>non suggerire</li><li>non correggere</li><li>non anticipare</li></ul>\n<p>Osserva semplicemente quello che fa. Come si muove. Cosa sceglie. Dove torna.</p>\n<p>E poi fatti una sola domanda: <em>«Che cosa mi sta raccontando questo bambino di sé, quando io smetto per un momento di dirgli cosa fare?»</em></p>",
  biblioteca_body: "<p>In queste lettere troverai anche le nuove risorse gratuite della mia Biblioteca T.A.T.A. Schede, piccole esperienze, strumenti di osservazione e materiali pratici pensati per genitori, tate ed educatori.</p>\n<p><strong>Non materiale da accumulare. Strumenti da usare nella vita vera.</strong></p>",
  podcast_body: "<p>Qualche volta continueremo la riflessione anche attraverso il mio podcast: <em>«Una casa alla volta. Un bambino alla volta. Una famiglia alla volta.»</em></p>\n<p>Ti segnalerò l'episodio più vicino al tema della lettera, così potrai scegliere se continuare a leggermi oppure ascoltarmi mentre cammini, guidi o hai finalmente dieci minuti per te.</p>",
  reply_body: "<p>C'è una cosa a cui tengo particolarmente. <strong>Puoi rispondermi.</strong></p>\n<p>Se una lettera ti fa pensare a qualcosa, raccontamelo. Se hai vissuto un episodio simile, scrivimi. Se non sei d'accordo con me, possiamo parlarne. Se c'è una domanda sull'infanzia che continua a tornarti in mente, mandamela.</p>\n<p>Perché non voglio costruire una newsletter che parla <em>alle</em> persone. Voglio costruire uno spazio che parla <em>con</em> le persone.</p>",
  services_body: "<p>E poi ci saranno momenti in cui una lettera non basta.</p>\n<p>Se senti di aver bisogno di uno sguardo esterno sulla vostra quotidianità, di mettere ordine in una difficoltà o semplicemente di capire da dove cominciare, puoi trovare sul mio sito i miei percorsi e le consulenze.</p>\n<p><em>Senza formule uguali per tutti. Partendo dal bambino reale e dalla famiglia reale che ho davanti.</em></p>",
  thanks_body: "<p>Allora cominciamo. Non so esattamente dove ci porteranno tutte queste lettere. Ma so da dove voglio partire: <strong>dalla relazione, dall'osservazione e dal rispetto dei tempi dell'infanzia.</strong></p>\n<p>Perché spesso i cambiamenti più importanti non iniziano facendo di più. Cominciano guardando diversamente ciò che abbiamo già davanti.</p>",
  closing_signature: "A presto,\nTeresa 💗",
  footer_tagline: "Una casa alla volta. Un bambino alla volta. Una famiglia alla volta.",
  cta_label: "",
  cta_url: "",
  scheduled_at: "",
  block_order: ["highlight", "quote", "chat", "sguardo", "tip", "biblioteca", "podcast", "reply", "services", "thanks"],
};

function loadStoredDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      // Migrate block_order if missing new blocks (backward compat)
      const defaultOrder = ["highlight", "quote", "chat", "sguardo", "tip", "biblioteca", "podcast", "reply", "services", "thanks"];
      if (Array.isArray(parsed.block_order)) {
        const missing = defaultOrder.filter((k) => !parsed.block_order.includes(k));
        parsed.block_order = [...parsed.block_order, ...missing];
      }
      return { ...emptyForm, ...parsed };
    }
  } catch { /* ignore */ }
  return null;
}

export default function AdminNewsletter() {
  const [form, setForm] = useState(() => loadStoredDraft() || emptyForm);
  const [previewHtml, setPreviewHtml] = useState("");
  const [subscribers, setSubscribers] = useState({ total: 0, subscribers: [] });
  const [campaigns, setCampaigns] = useState([]);
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [tab, setTab] = useState("compose"); // compose | history
  // AI assistant modal state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiKeywords, setAiKeywords] = useState("");
  const [aiToneNote, setAiToneNote] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const skipNextSaveRef = useRef(false);
  const navigate = useNavigate();

  const token = localStorage.getItem("tv_admin_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  // Auto-save form to localStorage whenever it changes (debounced)
  useEffect(() => {
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    const t = setTimeout(() => {
      try { localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(form)); } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [form]);

  const clearDraftAndReset = () => {
    skipNextSaveRef.current = true;
    try { localStorage.removeItem(DRAFT_STORAGE_KEY); } catch { /* ignore */ }
    setForm(emptyForm);
  };

  const resetComposer = () => {
    if (!window.confirm("Cancellare la bozza in composizione e ripartire da zero?")) return;
    clearDraftAndReset();
    toast.success("Composer ripulito");
  };

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    loadInitial();
  }, []);

  const loadInitial = async () => {
    try {
      const [subs, hist] = await Promise.all([
        axios.get(`${API_URL}/admin/newsletter/subscribers`, auth),
        axios.get(`${API_URL}/admin/newsletter/campaigns`, auth),
      ]);
      setSubscribers(subs.data);
      setCampaigns(hist.data);
      renderPreview(emptyForm);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("tv_admin_token");
        navigate("/admin/login");
      }
    }
  };

  const renderPreview = async (payload) => {
    try {
      const r = await axios.post(`${API_URL}/admin/newsletter/preview`, {
        subject: payload.subject || "Anteprima newsletter",
        preheader: payload.preheader,
        body_html: payload.body_html || "<p>(scrivi qui il tuo messaggio…)</p>",
        cta_label: payload.cta_label,
        cta_url: payload.cta_url,
        issue_number: payload.issue_number,
        greeting: payload.greeting,
        highlight_title: payload.highlight_title,
        highlight_body: payload.highlight_body,
        quote: payload.quote,
        tip_title: payload.tip_title,
        tip_body: payload.tip_body,
        closing_signature: payload.closing_signature,
        footer_tagline: payload.footer_tagline,
        block_order: payload.block_order,
        chat_body: payload.chat_body,
        sguardo_body: payload.sguardo_body,
        biblioteca_body: payload.biblioteca_body,
        podcast_body: payload.podcast_body,
        reply_body: payload.reply_body,
        services_body: payload.services_body,
        thanks_body: payload.thanks_body,
      }, auth);
      setPreviewHtml(r.data.html);
    } catch (err) {
      // ignore
    }
  };

  // Debounced live preview
  useEffect(() => {
    const t = setTimeout(() => renderPreview(form), 400);
    return () => clearTimeout(t);
  }, [form]);

  const doSend = async (test = false, schedule = false) => {
    if (!form.subject.trim() || !form.body_html.trim()) {
      toast.error("Oggetto e corpo del messaggio sono obbligatori");
      return;
    }
    if (schedule && !form.scheduled_at) {
      toast.error("Seleziona data e ora per la programmazione");
      return;
    }
    setSending(true);
    try {
      const payload = { ...form, test_send: test, audience: "all" };
      // Convert local datetime → ISO with tz
      if (schedule && form.scheduled_at) {
        payload.scheduled_at = new Date(form.scheduled_at).toISOString();
      } else {
        payload.scheduled_at = null;
      }
      const r = await axios.post(`${API_URL}/admin/newsletter/send`, payload, auth);
      const d = r.data;
      if (d.is_test) {
        toast.success(`📬 Test inviato a te! Controlla ${BRAND.email}. La bozza è ancora qui per modifiche 💗`);
        // NON pulire la bozza dopo un test — Teresa vuole poter iterare
      } else if (d.status === "scheduled") {
        toast.success(`⏰ Newsletter programmata per il ${new Date(d.scheduled_at).toLocaleString("it-IT")}`);
        clearDraftAndReset();
      } else {
        toast.success(`✅ Newsletter inviata a ${d.sent_count} iscritti${d.failed_count ? ` (${d.failed_count} falliti)` : ""}`);
        clearDraftAndReset();
      }
      const hist = await axios.get(`${API_URL}/admin/newsletter/campaigns`, auth);
      setCampaigns(hist.data);
      setShowConfirm(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'invio");
    } finally { setSending(false); }
  };

  const runAiAssist = async () => {
    const kw = aiKeywords.trim();
    if (!kw) { toast.error("Scrivi almeno una parola chiave"); return; }
    setAiLoading(true);
    try {
      const r = await axios.post(`${API_URL}/admin/newsletter/ai-suggest`, {
        keywords: kw,
        tone_note: aiToneNote,
      }, auth);
      const d = r.data || {};
      setForm((prev) => ({
        ...prev,
        subject: d.subject || prev.subject,
        preheader: d.preheader || prev.preheader,
        issue_number: d.issue_number || prev.issue_number,
        body_html: d.body_html || prev.body_html,
        highlight_title: d.highlight_title || prev.highlight_title,
        highlight_body: d.highlight_body || prev.highlight_body,
        quote: d.quote || prev.quote,
        chat_body: d.chat_body || prev.chat_body,
        sguardo_body: d.sguardo_body || prev.sguardo_body,
        tip_title: d.tip_title || prev.tip_title,
        tip_body: d.tip_body || prev.tip_body,
        biblioteca_body: d.biblioteca_body || prev.biblioteca_body,
        podcast_body: d.podcast_body || prev.podcast_body,
        reply_body: d.reply_body || prev.reply_body,
        services_body: d.services_body || prev.services_body,
        thanks_body: d.thanks_body || prev.thanks_body,
      }));
      toast.success("Bozza generata! Ora rileggi e adatta come vuoi 💗");
      setAiOpen(false);
      setAiKeywords("");
      setAiToneNote("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore assistente AI");
    } finally { setAiLoading(false); }
  };

  const cancelScheduled = async (id) => {
    if (!window.confirm("Annullare questo invio programmato?")) return;
    try {
      await axios.post(`${API_URL}/admin/newsletter/campaigns/${id}/cancel`, {}, auth);
      toast.success("Invio programmato annullato");
      const hist = await axios.get(`${API_URL}/admin/newsletter/campaigns`, auth);
      setCampaigns(hist.data);
    } catch (err) {
      toast.error("Errore nell'annullamento");
    }
  };

  const logout = () => {
    localStorage.removeItem("tv_admin_token");
    navigate("/admin/login");
  };

  const insertTag = (open, close = "") => {
    // simple insert wrapping selection with tags
    const textarea = document.getElementById("newsletter-body");
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const val = form.body_html;
    const selected = val.substring(start, end);
    const insert = close ? `${open}${selected}${close}` : open;
    const newVal = val.substring(0, start) + insert + val.substring(end);
    setForm({ ...form, body_html: newVal });
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + open.length, start + open.length + selected.length);
    }, 10);
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] p-6 md:p-10" data-testid="admin-newsletter-page">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <img src={BRAND.logo} alt="" className="h-12 w-12" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768]">— Newsletter T.A.T.A.</p>
              <h1 className="font-display text-3xl md:text-4xl text-[#3D2140]">Invio Newsletter</h1>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/admin" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]"><ArrowLeft size={14} /> Dashboard</Link>
            <Link to="/admin/statistiche" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]"><BarChart3 size={14} /> Statistiche</Link>
            <Link to="/admin/risorse" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]"><BookOpen size={14} /> Risorse</Link>
            <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]"><LogOut size={14} /> Esci</button>
          </div>
        </div>

        {/* KPI + Tabs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-5 border border-[#3D2140]/8">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#3D2140]/60 font-semibold flex items-center gap-1.5"><Users size={12} /> Iscritti attivi</p>
            <p className="font-display text-4xl text-[#FF2768] mt-1">{subscribers.total}</p>
            <p className="text-xs text-[#3D2140]/50 mt-2">Riceveranno la tua prossima email</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#3D2140]/8">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#3D2140]/60 font-semibold flex items-center gap-1.5"><Mail size={12} /> Newsletter inviate</p>
            <p className="font-display text-4xl text-[#81C3C9] mt-1">{campaigns.filter(c => !c.is_test).length}</p>
            <p className="text-xs text-[#3D2140]/50 mt-2">Escluse le prove</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#3D2140]/8">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#3D2140]/60 font-semibold flex items-center gap-1.5"><TestTube size={12} /> Test effettuati</p>
            <p className="font-display text-4xl text-[#3D2140] mt-1">{campaigns.filter(c => c.is_test).length}</p>
            <p className="text-xs text-[#3D2140]/50 mt-2">Solo a te, per provare</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 border-b border-[#3D2140]/10">
          <button onClick={() => setTab("compose")} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "compose" ? "text-[#FF2768] border-[#FF2768]" : "text-[#3D2140]/50 border-transparent"}`} data-testid="tab-compose">
            <Sparkles size={14} className="inline mr-1" /> Componi
          </button>
          <button onClick={() => setTab("history")} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "history" ? "text-[#FF2768] border-[#FF2768]" : "text-[#3D2140]/50 border-transparent"}`} data-testid="tab-history">
            <History size={14} className="inline mr-1" /> Storico ({campaigns.length})
          </button>
          <button
            onClick={() => setAiOpen(true)}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#FF2768] to-[#e91e5e] text-white text-xs font-semibold shadow-sm hover:shadow-md"
            data-testid="btn-ai-assist"
            title="Genera una bozza di newsletter con l'AI"
          >
            <Wand2 size={13} /> Aiutami a scrivere
          </button>
          <button
            onClick={resetComposer}
            className="px-3 py-2 text-xs text-[#3D2140]/60 hover:text-[#FF2768] font-semibold uppercase tracking-widest"
            data-testid="btn-reset-composer"
            title="Cancella la bozza in composizione"
          >
            ✕ Ricomincia
          </button>
        </div>

        {tab === "compose" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LEFT: Composer */}
            <div className="bg-white rounded-2xl border border-[#3D2140]/8 p-5 md:p-6 space-y-4" data-testid="composer">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Oggetto *</label>
                <input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Un piccolo dono per te 💗"
                  className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-[#3D2140] outline-none focus:border-[#FF2768]"
                  data-testid="input-subject"
                />
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Preheader (anteprima nella casella email)</label>
                <input
                  value={form.preheader}
                  onChange={(e) => setForm({ ...form, preheader: e.target.value })}
                  placeholder="Il testo che compare accanto all'oggetto nell'inbox"
                  className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-[#3D2140] outline-none focus:border-[#FF2768]"
                  data-testid="input-preheader"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Numero (opzionale)</label>
                  <input
                    value={form.issue_number}
                    onChange={(e) => setForm({ ...form, issue_number: e.target.value })}
                    placeholder="NUMERO 1 – TITOLO"
                    className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-[#3D2140] outline-none focus:border-[#FF2768] text-sm"
                    data-testid="input-issue-number"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Saluto (in corsivo)</label>
                  <input
                    value={form.greeting}
                    onChange={(e) => setForm({ ...form, greeting: e.target.value })}
                    placeholder="Ciao,"
                    className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-[#3D2140] outline-none focus:border-[#FF2768] text-sm"
                    data-testid="input-greeting"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Corpo del messaggio *</label>
                {/* Toolbar */}
                <div className="flex flex-wrap gap-1 mb-2">
                  <ToolBtn onClick={() => insertTag("<strong>", "</strong>")} title="Grassetto"><b>B</b></ToolBtn>
                  <ToolBtn onClick={() => insertTag("<em>", "</em>")} title="Corsivo"><i>I</i></ToolBtn>
                  <ToolBtn onClick={() => insertTag("<p>", "</p>")} title="Paragrafo">¶</ToolBtn>
                  <ToolBtn onClick={() => insertTag("<h3 style=\"color:#FF2768;font-family:Georgia,serif;\">", "</h3>")} title="Titolo">H</ToolBtn>
                  <ToolBtn onClick={() => insertTag("<ul><li>", "</li></ul>")} title="Lista">•</ToolBtn>
                  <ToolBtn onClick={() => insertTag("<blockquote style=\"border-left:4px solid #FF2768;padding:8px 16px;font-style:italic;color:#3D2140;\">", "</blockquote>")} title="Citazione">"</ToolBtn>
                  <ToolBtn onClick={() => {
                    const url = prompt("URL del link:");
                    if (url) insertTag(`<a href="${url}" style="color:#FF2768;">`, "</a>");
                  }} title="Link">🔗</ToolBtn>
                  <ToolBtn onClick={() => {
                    const url = prompt("URL immagine:");
                    if (url) insertTag(`<img src="${url}" style="max-width:100%;border-radius:12px;margin:16px 0;" alt="" />`);
                  }} title="Immagine">🖼</ToolBtn>
                </div>
                <textarea
                  id="newsletter-body"
                  value={form.body_html}
                  onChange={(e) => setForm({ ...form, body_html: e.target.value })}
                  rows={14}
                  className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-[#3D2140] outline-none focus:border-[#FF2768] font-mono text-sm leading-relaxed resize-none"
                  data-testid="input-body"
                />
                <p className="text-[11px] text-[#3D2140]/50 mt-1">Puoi usare HTML semplice: &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;a&gt;. Usa la toolbar sopra per aiutarti.</p>
              </div>

              {/* Blocchi opzionali del template "Una lettera alla volta" */}
              <details className="border border-[#3D2140]/10 rounded-xl overflow-hidden">
                <summary className="cursor-pointer px-4 py-3 bg-[#FFF5F7] font-semibold text-[#FF2768] text-sm select-none">💗 Box "Perché una lettera?" (opzionale)</summary>
                <div className="p-4 space-y-3">
                  <input
                    value={form.highlight_title}
                    onChange={(e) => setForm({ ...form, highlight_title: e.target.value })}
                    placeholder="Titolo del box (es. PERCHÉ UNA LETTERA?)"
                    className="w-full px-4 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm outline-none focus:border-[#FF2768]"
                    data-testid="input-highlight-title"
                  />
                  <textarea
                    value={form.highlight_body}
                    onChange={(e) => setForm({ ...form, highlight_body: e.target.value })}
                    placeholder="<p>Da più di vent'anni entro nelle case...</p><ul><li>Come faccio a farlo dormire?</li></ul>"
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm font-mono outline-none focus:border-[#FF2768] resize-none"
                    data-testid="input-highlight-body"
                  />
                </div>
              </details>

              <details className="border border-[#3D2140]/10 rounded-xl overflow-hidden">
                <summary className="cursor-pointer px-4 py-3 bg-[#FFF5F7] font-semibold text-[#FF2768] text-sm select-none">"💬 Citazione in evidenza (opzionale)</summary>
                <div className="p-4">
                  <input
                    value={form.quote}
                    onChange={(e) => setForm({ ...form, quote: e.target.value })}
                    placeholder="Come posso stare accanto a questo bambino senza perdere di vista lui... e senza perdere me?"
                    className="w-full px-4 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm outline-none focus:border-[#FF2768]"
                    data-testid="input-quote"
                  />
                </div>
              </details>

              <details className="border border-[#3D2140]/10 rounded-xl overflow-hidden">
                <summary className="cursor-pointer px-4 py-3 bg-[#FFF9EC] font-semibold text-[#B8860B] text-sm select-none">🌱 "Piccola cosa da provare già oggi" (opzionale)</summary>
                <div className="p-4 space-y-3">
                  <input
                    value={form.tip_title}
                    onChange={(e) => setForm({ ...form, tip_title: e.target.value })}
                    placeholder="Piccola cosa da provare già oggi"
                    className="w-full px-4 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm outline-none focus:border-[#FF2768]"
                    data-testid="input-tip-title"
                  />
                  <textarea
                    value={form.tip_body}
                    onChange={(e) => setForm({ ...form, tip_body: e.target.value })}
                    placeholder="<p>Quando oggi sarai accanto a un bambino, prova per qualche minuto a non anticipare...</p>"
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm font-mono outline-none focus:border-[#FF2768] resize-none"
                    data-testid="input-tip-body"
                  />
                </div>
              </details>

              {[
                { key: "chat_body", label: "Due parole tra noi", bg: "#FFF5F7", color: "#FF2768" },
                { key: "sguardo_body", label: "Lo sguardo educativo", bg: "#E8F5F6", color: "#4E9EA5" },
                { key: "biblioteca_body", label: "Dalla Biblioteca T.A.T.A.", bg: "#FFF5F7", color: "#FF2768" },
                { key: "podcast_body", label: "Nelle cuffie", bg: "#E8F5F6", color: "#4E9EA5" },
                { key: "reply_body", label: "Puoi rispondermi", bg: "#FFF5F7", color: "#FF2768" },
                { key: "services_body", label: "Se hai bisogno di me", bg: "#E8F5F6", color: "#4E9EA5" },
                { key: "thanks_body", label: "Grazie di cuore", bg: "#FFF5F7", color: "#FF2768" },
              ].map((b) => (
                <details key={b.key} className="border border-[#3D2140]/10 rounded-xl overflow-hidden">
                  <summary className="cursor-pointer px-4 py-3 font-semibold text-sm select-none" style={{ background: b.bg, color: b.color }}>
                    {b.label} (opzionale)
                  </summary>
                  <div className="p-4">
                    <textarea
                      value={form[b.key] || ""}
                      onChange={(e) => setForm({ ...form, [b.key]: e.target.value })}
                      placeholder={`<p>Contenuto del blocco "${b.label}"...</p>`}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm font-mono outline-none focus:border-[#FF2768] resize-none"
                      data-testid={`input-${b.key.replace("_body","")}-body`}
                    />
                    <p className="text-[11px] text-[#3D2140]/50 mt-1">Il titolo del blocco è fisso: <strong>{b.label}</strong>. Lascia vuoto per nascondere questo blocco nella lettera.</p>
                  </div>
                </details>
              ))}

              <details className="border border-[#3D2140]/10 rounded-xl overflow-hidden" open>
                <summary className="cursor-pointer px-4 py-3 bg-[#E8F5F6] font-semibold text-[#4E9EA5] text-sm select-none">
                  <MoveVertical size={14} className="inline mr-1" /> Blocchi della lettera — attiva/disattiva e ordina
                </summary>
                <div className="p-4 space-y-2">
                  <p className="text-xs text-[#3D2140]/60 mb-3">
                    Spunta i blocchi da <strong>includere</strong> in questo numero (il testo resta salvato anche se disattivi). Usa le frecce per riordinare quelli attivi.
                  </p>
                  {(() => {
                    const ALL_BLOCKS = [
                      { key: "highlight", name: "💗 Perché una lettera? (Editoriale)", filledFn: (f) => !!(f.highlight_title || f.highlight_body) },
                      { key: "quote", name: "✨ Citazione della lettera", filledFn: (f) => !!f.quote },
                      { key: "chat", name: "☕ Due parole tra noi", filledFn: (f) => !!f.chat_body },
                      { key: "sguardo", name: "🔎 Lo sguardo educativo", filledFn: (f) => !!f.sguardo_body },
                      { key: "tip", name: "🌱 Piccola cosa da provare oggi", filledFn: (f) => !!(f.tip_title || f.tip_body) },
                      { key: "biblioteca", name: "📚 Dalla Biblioteca T.A.T.A.", filledFn: (f) => !!f.biblioteca_body },
                      { key: "podcast", name: "🎧 Nelle cuffie", filledFn: (f) => !!f.podcast_body },
                      { key: "reply", name: "💌 Puoi rispondermi", filledFn: (f) => !!f.reply_body },
                      { key: "services", name: "🌱 Se hai bisogno di me", filledFn: (f) => !!f.services_body },
                      { key: "thanks", name: "💗 Grazie di cuore", filledFn: (f) => !!f.thanks_body },
                    ];
                    const active = new Set(form.block_order);
                    // Show active first (in their order) then inactive
                    const activeItems = form.block_order
                      .map((k) => ALL_BLOCKS.find((b) => b.key === k))
                      .filter(Boolean);
                    const inactiveItems = ALL_BLOCKS.filter((b) => !active.has(b.key));
                    const toggleBlock = (key) => {
                      if (active.has(key)) {
                        setForm({ ...form, block_order: form.block_order.filter((k) => k !== key) });
                      } else {
                        setForm({ ...form, block_order: [...form.block_order, key] });
                      }
                    };
                    const moveBlock = (key, delta) => {
                      const idx = form.block_order.indexOf(key);
                      const next = idx + delta;
                      if (idx < 0 || next < 0 || next >= form.block_order.length) return;
                      const arr = [...form.block_order];
                      [arr[idx], arr[next]] = [arr[next], arr[idx]];
                      setForm({ ...form, block_order: arr });
                    };
                    return (
                      <>
                        {activeItems.length > 0 && (
                          <div className="space-y-1.5">
                            <p className="text-[10px] uppercase tracking-widest text-[#4E9EA5] font-bold mb-1">✓ Attivi in questo numero ({activeItems.length})</p>
                            {activeItems.map((b, idx) => (
                              <div key={b.key} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-[#4E9EA5]/30" data-testid={`block-row-${b.key}`}>
                                <input
                                  type="checkbox"
                                  checked
                                  onChange={() => toggleBlock(b.key)}
                                  className="h-4 w-4 accent-[#4E9EA5] cursor-pointer"
                                  data-testid={`block-check-${b.key}`}
                                  aria-label={`Disattiva ${b.name}`}
                                />
                                <span className="text-xs font-bold text-[#3D2140]/50 w-6">{idx + 1}.</span>
                                <span className={`flex-1 text-sm ${b.filledFn(form) ? "text-[#3D2140] font-semibold" : "text-[#3D2140]/40"}`}>
                                  {b.name} {!b.filledFn(form) && <em className="text-xs">(vuoto — non apparirà)</em>}
                                </span>
                                <button type="button" onClick={() => moveBlock(b.key, -1)} disabled={idx === 0}
                                  className="p-1.5 rounded-full bg-[#FAF8F5] hover:bg-[#FF2768] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                                  data-testid={`move-up-${b.key}`} title="Sposta su"><ChevronUp size={14} /></button>
                                <button type="button" onClick={() => moveBlock(b.key, 1)} disabled={idx === activeItems.length - 1}
                                  className="p-1.5 rounded-full bg-[#FAF8F5] hover:bg-[#FF2768] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                                  data-testid={`move-down-${b.key}`} title="Sposta giù"><ChevronDown size={14} /></button>
                              </div>
                            ))}
                          </div>
                        )}
                        {inactiveItems.length > 0 && (
                          <div className="space-y-1.5 mt-4 pt-4 border-t border-[#3D2140]/10">
                            <p className="text-[10px] uppercase tracking-widest text-[#3D2140]/40 font-bold mb-1">○ Disattivati ({inactiveItems.length})</p>
                            {inactiveItems.map((b) => (
                              <div key={b.key} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#FAF8F5]/50 border border-dashed border-[#3D2140]/10 opacity-70" data-testid={`block-row-${b.key}`}>
                                <input
                                  type="checkbox"
                                  checked={false}
                                  onChange={() => toggleBlock(b.key)}
                                  className="h-4 w-4 accent-[#FF2768] cursor-pointer"
                                  data-testid={`block-check-${b.key}`}
                                  aria-label={`Attiva ${b.name}`}
                                />
                                <span className={`flex-1 text-sm line-through ${b.filledFn(form) ? "text-[#3D2140]/60" : "text-[#3D2140]/30"}`}>
                                  {b.name} {b.filledFn(form) && <em className="text-[10px] not-italic uppercase tracking-widest text-[#4E9EA5] no-underline">· testo salvato</em>}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-3 pt-3 mt-3 border-t border-[#3D2140]/10">
                          <button
                            type="button"
                            onClick={() => setForm({ ...form, block_order: ALL_BLOCKS.map((b) => b.key) })}
                            className="text-xs text-[#4E9EA5] hover:text-[#FF2768] underline"
                            data-testid="btn-blocks-all"
                          >Attiva tutti</button>
                          <button
                            type="button"
                            onClick={() => setForm({ ...form, block_order: [] })}
                            className="text-xs text-[#3D2140]/60 hover:text-[#FF2768] underline"
                            data-testid="btn-blocks-none"
                          >Disattiva tutti</button>
                          <button
                            type="button"
                            onClick={() => setForm({ ...form, block_order: ALL_BLOCKS.map((b) => b.key) })}
                            className="text-xs text-[#3D2140]/60 hover:text-[#FF2768] underline"
                          >Ripristina ordine di default</button>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </details>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Chiusura (in corsivo rosa)</label>
                  <textarea
                    value={form.closing_signature}
                    onChange={(e) => setForm({ ...form, closing_signature: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm outline-none focus:border-[#FF2768] resize-none"
                    data-testid="input-closing"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Frase finale turchese</label>
                  <textarea
                    value={form.footer_tagline}
                    onChange={(e) => setForm({ ...form, footer_tagline: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm outline-none focus:border-[#FF2768] resize-none"
                    data-testid="input-tagline"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-[#3D2140]/10">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Testo pulsante (opzionale)</label>
                  <input
                    value={form.cta_label}
                    onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                    placeholder="Es. Scarica la risorsa"
                    className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-[#3D2140] outline-none focus:border-[#FF2768]"
                    data-testid="input-cta-label"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Link pulsante</label>
                  <input
                    value={form.cta_url}
                    onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
                    placeholder="https://…"
                    className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-[#3D2140] outline-none focus:border-[#FF2768]"
                    data-testid="input-cta-url"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="pt-4 border-t border-[#3D2140]/10">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  <div className="md:col-span-2">
                    <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5"><Clock size={12} className="inline mr-1" /> Programma per (opzionale)</label>
                    <input
                      type="datetime-local"
                      value={form.scheduled_at}
                      onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                      min={new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16)}
                      className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-[#3D2140] outline-none focus:border-[#FF2768]"
                      data-testid="input-scheduled-at"
                    />
                    <p className="text-[11px] text-[#3D2140]/50 mt-1">Se compilato, la newsletter parte automaticamente alla data indicata</p>
                  </div>
                  {form.scheduled_at && (
                    <div className="md:self-end">
                      <button
                        onClick={() => setForm({ ...form, scheduled_at: "" })}
                        className="text-xs text-[#3D2140]/60 hover:text-[#FF2768] underline"
                      >Rimuovi programmazione</button>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => doSend(true, false)}
                    disabled={sending}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border-2 border-[#81C3C9] text-[#3D2140] font-semibold hover:bg-[#81C3C9]/20 disabled:opacity-50"
                    data-testid="btn-test-send"
                  >
                    <TestTube size={14} /> {sending ? "Invio…" : "Invia prova a me"}
                  </button>
                  {form.scheduled_at ? (
                    <button
                      onClick={() => doSend(false, true)}
                      disabled={sending}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#3D2140] text-white font-semibold hover:bg-black disabled:opacity-50"
                      data-testid="btn-schedule"
                    >
                      <Clock size={14} /> Programma invio ({subscribers.total})
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowConfirm(true)}
                      disabled={sending}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF2768] text-white font-semibold hover:bg-[#E01F5A] disabled:opacity-50"
                      data-testid="btn-send"
                    >
                      <Send size={14} /> Invia subito a tutti ({subscribers.total})
                    </button>
                  )}
                  <p className="text-xs text-[#3D2140]/50">📬 <em>"Invia prova"</em> manda solo a te</p>
                </div>
              </div>
            </div>

            {/* RIGHT: Live preview */}
            <div className="bg-white rounded-2xl border border-[#3D2140]/8 overflow-hidden" data-testid="preview-panel">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-[#3D2140]/8 bg-[#FAF8F5]">
                <Eye size={16} className="text-[#FF2768]" />
                <p className="text-sm font-semibold text-[#3D2140]">Anteprima live</p>
                <span className="ml-auto text-[10px] uppercase tracking-widest text-[#3D2140]/50">Esattamente come la vedranno gli iscritti</span>
              </div>
              <iframe
                srcDoc={previewHtml}
                title="Newsletter preview"
                className="w-full"
                style={{ height: "800px", border: "0", background: "#FAF8F5" }}
                data-testid="preview-iframe"
              />
            </div>
          </div>
        )}

        {tab === "history" && (
          <div className="bg-white rounded-2xl border border-[#3D2140]/8 overflow-hidden" data-testid="history-panel">
            {campaigns.length === 0 && <p className="p-10 text-center text-[#3D2140]/60 font-editorial italic text-lg">Nessuna newsletter inviata finora. Quando inizierai a spedire, la troverai qui 🌿</p>}
            {campaigns.map((c) => {
              const isScheduled = c.status === "scheduled";
              const isCancelled = c.status === "cancelled";
              const badgeColor = isScheduled ? "#F59E0B" : (isCancelled ? "#9CA3AF" : (c.is_test ? "#81C3C9" : "#10B981"));
              const badgeLabel = isScheduled ? "Programmata" : (isCancelled ? "Annullata" : (c.is_test ? "Test" : "Inviata"));
              return (
                <div key={c.id} className="p-5 border-t border-[#3D2140]/5 first:border-0 flex flex-wrap items-center gap-4 hover:bg-[#FAF8F5]/50">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="inline-block px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold text-white" style={{ background: badgeColor }}>
                        {isScheduled ? <><Clock size={10} className="inline" /> {badgeLabel}</> : badgeLabel}
                      </span>
                      <p className="font-semibold text-[#3D2140]">{c.subject}</p>
                    </div>
                    <p className="text-xs text-[#3D2140]/60">
                      {isScheduled
                        ? `Programmata per: ${new Date(c.scheduled_at).toLocaleString("it-IT", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}`
                        : new Date(c.sent_at).toLocaleString("it-IT", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                  <div className="text-right text-xs">
                    {isScheduled ? (
                      <p className="text-[#F59E0B] font-semibold">In attesa…</p>
                    ) : isCancelled ? (
                      <p className="text-[#9CA3AF]">—</p>
                    ) : (
                      <>
                        <p className="text-[#3D2140]">📬 {c.sent_count} inviate</p>
                        {c.failed_count > 0 && <p className="text-red-500">{c.failed_count} fallite</p>}
                        <p className="text-[#3D2140]/50">su {c.recipients_count} destinatari</p>
                      </>
                    )}
                  </div>
                  {isScheduled && (
                    <button
                      onClick={() => cancelScheduled(c.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#F59E0B]/40 text-[#F59E0B] text-xs font-semibold hover:bg-[#F59E0B]/10"
                      data-testid={`btn-cancel-${c.id}`}
                    >
                      <XCircle size={12} /> Annulla
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Confirmation modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#3D2140]/70 backdrop-blur-sm" onClick={() => !sending && setShowConfirm(false)}>
          <div className="bg-white rounded-3xl max-w-md w-full p-8 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="confirm-modal">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-2">— Conferma invio</p>
            <h3 className="font-display text-3xl text-[#3D2140] mb-3 leading-tight">Sei pronta a inviare?</h3>
            <p className="text-[#3D2140]/70 mb-2">Stai per mandare la newsletter <strong>"{form.subject || "(senza oggetto)"}"</strong></p>
            <p className="text-[#3D2140]/70 mb-6">A <strong>{subscribers.total} iscritti</strong> attivi.</p>
            <div className="p-4 rounded-xl bg-[#FFE45C]/30 border border-[#F59E0B]/40 text-xs text-[#3D2140] mb-6">
              💡 <strong>Consiglio:</strong> se non l'hai ancora fatto, prova prima con <em>"Invia prova a me"</em> per verificare come appare nella tua inbox.
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={sending}
                className="flex-1 px-6 py-3 rounded-full border border-[#3D2140]/15 text-[#3D2140] font-semibold hover:bg-[#FAF8F5]"
                data-testid="btn-cancel"
              >Annulla</button>
              <button
                onClick={() => doSend(false, false)}
                disabled={sending}
                className="flex-1 px-6 py-3 rounded-full bg-[#FF2768] text-white font-semibold hover:bg-[#E01F5A] disabled:opacity-50 inline-flex items-center justify-center gap-2"
                data-testid="btn-confirm-send"
              >
                {sending ? "Invio in corso…" : <><Send size={14} /> Invia ora</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Assistant Modal */}
      {aiOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !aiLoading && setAiOpen(false)} data-testid="ai-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#FF2768] to-[#e91e5e] flex items-center justify-center text-white">
                <Wand2 size={18} />
              </div>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF2768] font-bold">Assistente editoriale</p>
                <h3 className="font-display text-xl text-[#3D2140]">Aiutami a scrivere</h3>
              </div>
            </div>
            <p className="text-sm text-[#3D2140]/70 mb-5 leading-relaxed">
              Racconta di cosa vuoi parlare in questo numero. L'assistente scriverà una bozza nel tono di <em>Una lettera alla volta</em> — poi rileggi, adatta, rendila tua.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Parole chiave / tema *</label>
                <input
                  value={aiKeywords}
                  onChange={(e) => setAiKeywords(e.target.value)}
                  placeholder="es. autonomia, sonno del neonato, capricci del bambino di 2 anni..."
                  className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm outline-none focus:border-[#FF2768]"
                  data-testid="input-ai-keywords"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Note aggiuntive (opzionali)</label>
                <textarea
                  value={aiToneNote}
                  onChange={(e) => setAiToneNote(e.target.value)}
                  placeholder="es. Voglio partire da una domanda che mi ha fatto una mamma..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm outline-none focus:border-[#FF2768] resize-none"
                  data-testid="input-ai-tone"
                />
              </div>
            </div>

            <div className="mt-5 p-3 rounded-xl bg-[#FFE45C]/30 border border-[#F59E0B]/30 text-[11px] text-[#3D2140]/80 leading-relaxed">
              ✨ L'AI compilerà oggetto, preheader, apertura, box editoriale, citazione e tutti i blocchi tematici della newsletter. <strong>Sostituirà la bozza attuale.</strong>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setAiOpen(false)}
                disabled={aiLoading}
                className="flex-1 px-5 py-3 rounded-full border border-[#3D2140]/15 text-[#3D2140] font-semibold hover:bg-[#FAF8F5]"
                data-testid="btn-ai-cancel"
              >Annulla</button>
              <button
                onClick={runAiAssist}
                disabled={aiLoading || !aiKeywords.trim()}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-[#FF2768] to-[#e91e5e] text-white font-semibold disabled:opacity-50"
                data-testid="btn-ai-generate"
              >
                {aiLoading ? "Sto scrivendo..." : <><Wand2 size={14} /> Genera bozza</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ToolBtn({ onClick, children, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="w-8 h-8 rounded-lg bg-[#FAF8F5] border border-[#3D2140]/10 hover:border-[#FF2768] hover:text-[#FF2768] text-[#3D2140] text-sm flex items-center justify-center transition-colors"
    >
      {children}
    </button>
  );
}
